import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { Component, Input, OnDestroy } from '@angular/core';

import { AxonEventModel, FsAxonService } from '@lib/axon';
import { FsTimeService } from '@lib/common/services';
import {
  DeckNotificationHistoricalData,
  DeckNotificationTooltip,
} from '../../types/deck-notification';
import { FsMap } from '../../types/map';
import { FsScreenPoint } from '../../core/screen-point';
import { mapToRouteDataItem, SnRouterHelperService } from '@lib/state/router';
import { AxonNotificationIconService } from '@lib/axon/services/axon-notification-icon.service';
import { NOTIFICATION_MESSAGES } from '@lib/axon/constants/notification-messages.constants';

@Component({
  selector: 'fs-deck-notification',
  templateUrl: './deck-notification.component.html',
  styleUrls: ['./deck-notification.component.scss'],
})
export class FsDeckNotificationComponent implements OnDestroy {
  @Input() deviceNotificationMap: Map<string, AxonEventModel[]>;

  public map: FsMap;
  public content = '';
  public tooltips: DeckNotificationTooltip[] = [];

  private _messages = NOTIFICATION_MESSAGES;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _axonService: FsAxonService,
    private readonly _routerHelper: SnRouterHelperService,
    private readonly _timeService: FsTimeService,
    private readonly _axonNotificationIconService: AxonNotificationIconService,
  ) {
    this._routeChangeSubscribe();
  }

  ngOnDestroy(): void {
    this._removeTooltips();
    this._axonService.clearDeviceNotificationMap();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public listenForNotifications(fsMap: FsMap): void {
    this.map = fsMap;
    this._generateNotificationContent();

    this.map?.on('boundsChange').subscribe(() => {
      this._generateNotificationContent();
    });

    this._axonService.devicesUpdated$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._generateNotificationContent();
    });
  }

  private _removeTooltips() {
    if (this.tooltips.length > 0) {
      this.tooltips.forEach((tooltip) => {
        document.body.removeChild(tooltip.element);
      });
      this.tooltips = [];
    }
  }

  private _routeChangeSubscribe(): void {
    this._routerHelper.routeChange$
      .pipe(mapToRouteDataItem('snRoute', true), distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe(() => {
        this._removeTooltips();
      });
  }

  private _formatHistoricalData(
    historicalData: AxonEventModel,
    device: any,
  ): DeckNotificationHistoricalData {
    return {
      device: {
        deviceId: historicalData.attributes.device.deviceId,
        deviceType: historicalData.attributes.device.deviceType,
      },
      message: historicalData.attributes.category,
      messageTranslation:
        this._messages[historicalData.attributes.category] || historicalData.attributes.category,
      ownerName: this._generateDeviceName(device),
      notification: {
        category: historicalData.attributes.category,
        notificationReceivedTime: historicalData.attributes.timestamp,
        timeOccurred: historicalData.attributes.timestamp,
        deviceId: historicalData.attributes.device.deviceId,
        deviceType: historicalData.attributes.device.deviceType,
        type: historicalData.type,
      },
    };
  }

  private _generateNotificationContent() {
    if (this.deviceNotificationMap && this._axonService.deviceNotificationMap) {
      this.deviceNotificationMap.forEach((value, key) => {
        const device = this._axonService.deviceMap.get(key);

        if (device) {
          const icons: string[] = [];
          const historicalData: DeckNotificationHistoricalData[] = [];
          value.forEach((event) => {
            if (
              this._timeService.minutesSinceUnix(event.attributes.timestamp) < 300 &&
              (device.attributes.status === 'RECORDING' || device.attributes.status === 'BUFFERING')
            ) {
              const data = this._formatHistoricalData(event, device);
              historicalData.push(data);
              const existingTooltipIndex = this.tooltips.findIndex((tooltip) => {
                return tooltip.id === data.device.deviceId;
              });

              if (existingTooltipIndex > -1) {
                this.tooltips[existingTooltipIndex].position = [
                  device.attributes.location?.longitude,
                  device.attributes.location?.latitude,
                ];
                const icon = this._axonNotificationIconService.getIconClass(data.notification.category);
                if (!icons.includes(icon)) icons.push(icon);
              } else {
                if (data.notification.category === 'WatchMeCancel') {
                  const iconToRemove = icons.findIndex((icon) => icon === 'fa-hand');
                  icons.splice(iconToRemove, 1);
                } else {
                  const icon = this._axonNotificationIconService.getIconClass(data.notification.category);
                  if (!icons.includes(icon)) icons.push(icon);
                }
              }
            }
          });

          if (icons.length === 0) {
            const tooltipToDelete = this.tooltips.findIndex(
              (tooltip) => tooltip.id === device.axonDeviceId,
            );
            if (document.body.contains(this.tooltips[tooltipToDelete]?.element)) {
              document.body.removeChild(this.tooltips[tooltipToDelete].element);
            }
            this.tooltips.splice(tooltipToDelete, 1);
          } else {
            const tooltip = this._createTooltipElement(icons);
            const notificationTooltip = {
              id: device.axonDeviceId,
              element: tooltip,
              position: [device.attributes.location.longitude, device.attributes.location.latitude],
              data: historicalData,
            };

            const tooltipIndex = this.tooltips.findIndex(
              (existingTooltip) => existingTooltip.id === device.axonDeviceId,
            );
            if (tooltipIndex > -1) {
              this.tooltips[tooltipIndex].position = notificationTooltip.position;
              this.tooltips[tooltipIndex].data = notificationTooltip.data;
            } else {
              this.tooltips.push(notificationTooltip);
              document.body.appendChild(tooltip);
            }
          }
        }
      });

      this.tooltips.forEach((t) => {
        const screenPoint = new FsScreenPoint(t.position, this.map);

        t.element.style.left = `${screenPoint.screenPoint?.x - 50}px`;
        t.element.style.top = `${screenPoint.screenPoint?.y - 65}px`;
      });
    }
  }

  private _createTooltipElement(icons: string[]): HTMLDivElement {
    const tooltipDivElements: string[] = icons.map((icon) => {
      return `<i class="fa-solid ${icon} fs-notification-banner-icon"></i>`;
    });
    const tooltip = document.createElement('div');
    tooltip.className = 'deck-notification';
    tooltip.innerHTML = tooltipDivElements.join('');
    tooltip.classList.add('fs__advanced-marker-icon-banner');

    return tooltip;
  }

  private _generateDeviceName(device: any): string {
    const assignee = device.attributes?.assignees;
    switch (device.deviceModel || device.deviceModelInternal) {
      case 'AXON_BODY_3':
      case 'AXON_BODY_4':
      case 'AXON_BODY_WORKFORCE':
      case 'AXON_BODY_WORKFORCE_MINI':
        if (assignee) {
          return `${assignee[0]?.firstName} ${assignee[0]?.lastName}`;
        }
        return 'Unassigned';
      case 'AXON_FLEET_3_HUB':
        if (device.attributes?.vehicle?.vehicleName.length > 0) {
          return `${device.attributes?.vehicle?.vehicleName}`;
        }
        return 'Unassigned';
      default:
        return 'Unassigned';
    }
  }

}
